// Dependencies
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useRouter } from "next/router";

import PropTypes from "prop-types";

// Material UI Components
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";

//  Material UI Icons
import CloseIcon from "@mui/icons-material/Close";

// Components
import ClearAllFiltersButton from "features/inventory/components/ClearAllFiltersButton/ClearAllFiltersButton.component";
import InventorySortDropdown from "features/inventory/components/InventorySortDropdown/InventorySortDropdown.component";
import InputSlider from "components/inputs/InputSlider/InputSlider.component";
import Transition from "components/modals/Transition.component";
import SearchButton from "features/inventory/components/SearchButton/SearchButton.component";
import FilterOption from "features/inventory/components/FilterOptions/FilterOption.component";
import FilterModalSection from "features/inventory/components/FilterModalSection/FilterModalSection.component";
import LocationForm from "features/inventory/components/LocationForm/LocationForm.component";
import Label from "components/typographies/Label/Label.component";

// Helpers
import {
  addQueryItem,
  replaceQueryItem,
  removeQueryOrSlugItem,
  replaceSlugAndQuery,
  addOrUpdateSlugValue,
  getPathnameItem,
} from "helpers/inventory-filters";
import useInventoryFilters from "hooks/inventory/useInventoryFilters.hook";
import { inventoryReducer } from "reducers/inventory-reducer";
import usePageWidth from "hooks/usePageWidth.hook";

// Styles
import { useStyles } from "./FilterModal.styles";

const maxYear = new Date().getFullYear() + 1;

const FilterModal = (props) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const loading = useSelector((s) => s.inventory.loading);
  const filterModalOpen = useSelector((s) => s.inventory.filterModalOpen);
  const { isDesktopWidth } = usePageWidth();

  // Props
  const { attributes, clearInventoryCards, listingsCount, locationFormProps } =
    props;
  // TODO: Remove button from this component

  // Reducer state

  const { filters: inventoryFilters } = useInventoryFilters();
  // Local State
  const classes = useStyles();

  const handleCheckbox = (e) => {
    const { checked } = e.target;
    clearInventoryCards();
    const payload = { key: e.target.name, value: e.target.value };
    const newQuery = addQueryItem(router.query, payload);
    if (checked) {
      router.replace(
        {
          pathname: "/cars/[[...slug]]",
          query: newQuery,
        },
        undefined,
        { shallow: true },
      );
    } else {
      const { newQuery: newRemovedQuery, newSlug } = removeQueryOrSlugItem(
        router.query,
        payload,
      );
      replaceSlugAndQuery(router, newSlug, newRemovedQuery);
    }
  };

  const handleSliderChange = (name, values, badValue) => {
    const payload = { key: name, value: values };
    const yearInPath = getPathnameItem(router.query.slug, "year");
    // If years are different remove pathName year
    clearInventoryCards();
    if (badValue) {
      const { newQuery, newSlug } = removeQueryOrSlugItem(
        router.query,
        payload,
      );
      replaceSlugAndQuery(router, newSlug, newQuery);
    } else {
      const newQuery = replaceQueryItem(router.query, {
        key: name,
        value: values,
      });
      // If start and end year are the same add year to pathname
      if (values[0] === values[1] && name === "year") {
        const newSlug = addOrUpdateSlugValue(
          router.query.slug,
          "year",
          values[0],
        );
        replaceSlugAndQuery(router, newSlug, newQuery);
      } else if (values[0] !== values[1] && name === "year" && yearInPath) {
        // If start and end year are different remove year from pathname
        const { newSlug } = removeQueryOrSlugItem(router.query, {
          key: "year",
          value: yearInPath,
        });
        replaceSlugAndQuery(router, newSlug, newQuery);
      } else {
        replaceSlugAndQuery(router, router.query.slug, newQuery);
      }
    }
  };

  const mapAttributesToCheckboxes = (attributeName) => {
    const returnItem = [];
    if (!attributes[attributeName]) return returnItem;
    returnItem.push(
      ...attributes[attributeName].map((item) => (
        <Grid
          key={`${attributeName}-${item.name}`}
          width={5}
          item
          xs={6}
          md={4}
        >
          <FormControlLabel
            control={
              <Checkbox
                onChange={handleCheckbox}
                key={`checkbox-${attributeName}-${item.name}`}
                name={attributeName}
                value={item.name}
                checked={inventoryFilters[attributeName]?.includes(item.name)}
                color="primary"
                disabled={loading.filters}
              />
            }
            data-cy={`filter-${attributeName}-${item.name}`}
            label={item.name}
          />
        </Grid>
      )),
    );
    return returnItem;
  };

  const handleClose = () => {
    dispatch(inventoryReducer.actions.setFilterModalOpen(false));
  };

  return (
    <span>
      <Dialog
        className={classes.filterModalContainer}
        fullScreen={!isDesktopWidth}
        onClose={handleClose}
        open={filterModalOpen}
        TransitionComponent={Transition}
      >
        <DialogTitle
          className={classes.filterModal}
          id="customized-dialog-title"
          onClose={handleClose}
        >
          <IconButton
            sx={{ padding: 0 }}
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            size="large"
          >
            <CloseIcon />
          </IconButton>
          <Label sx={{ margin: "auto" }} size="lg" weight="semibold">
            {isDesktopWidth ? "Filter" : "Sort & filter"}
          </Label>
        </DialogTitle>

        <DialogContent dividers className={classes.modalContents}>
          {!isDesktopWidth && (
            <FilterModalSection title="Sort by">
              <InventorySortDropdown
                dataCy="inventory-sort-type-modal"
                clearInventoryCards={clearInventoryCards}
              />
            </FilterModalSection>
          )}
          <FilterModalSection>
            <Grid item xs={12} sx={{ marginBottom: "10px" }}>
              <FilterOption
                clearInventoryCards={clearInventoryCards}
                className={
                  !getPathnameItem(router.query.slug, "make") &&
                  classes.unfocused
                }
                attributes={attributes}
                keyName="make"
                defaultValueLabel="Any make"
                indexOfPath={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FilterOption
                clearInventoryCards={clearInventoryCards}
                className={
                  !getPathnameItem(router.query.slug, "model") &&
                  classes.unfocused
                }
                attributes={attributes}
                keyName="model"
                defaultValueLabel="Any model"
                indexOfPath={3}
                isDisabled={!getPathnameItem(router.query.slug, "make")}
                fullWidth
              />
            </Grid>
          </FilterModalSection>
          <FilterModalSection title="Location">
            <Grid item xs={12}>
              <LocationForm
                locationFormProps={locationFormProps}
                clearInventoryCards={clearInventoryCards}
              />
            </Grid>
          </FilterModalSection>
          <FilterModalSection title="Condition">
            <Grid key="condition-new-grid" width={5} item xs={6} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleCheckbox}
                    key="checkbox-condition-new"
                    name="condition"
                    value="New"
                    checked={inventoryFilters.condition.includes("New")}
                    color="primary"
                    disabled={loading.filters}
                  />
                }
                label="New"
              />
            </Grid>
            <Grid key="condition-used-grid" width={5} item xs={6} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleCheckbox}
                    key="checkbox-condition-used"
                    name="condition"
                    value="Used"
                    checked={inventoryFilters.condition.includes("Used")}
                    color="primary"
                    disabled={loading.filters}
                  />
                }
                label="Used"
              />
            </Grid>
          </FilterModalSection>
          <FilterModalSection title="Body Type">
            {mapAttributesToCheckboxes("bodyType")}
          </FilterModalSection>
          <FilterModalSection title="Price">
            <InputSlider
              min={0}
              max={200000}
              step={500}
              values={inventoryFilters.price.map((price) => parseInt(price))}
              valuesChanged={handleSliderChange}
              formatNumbers
              name="price"
              thousands=","
              prefix="$"
            />
          </FilterModalSection>
          <FilterModalSection title="Kilometers">
            <InputSlider
              min={0}
              max={300000}
              step={500}
              values={inventoryFilters.kilometers.map((kilometers) =>
                parseInt(kilometers),
              )}
              valuesChanged={handleSliderChange}
              formatNumbers
              name="kilometers"
              thousands=","
              suffix="km"
            />
          </FilterModalSection>
          <FilterModalSection title="Year">
            <InputSlider
              min={1950}
              max={maxYear}
              step={1}
              values={inventoryFilters.year}
              valuesChanged={handleSliderChange}
              formatNumbers={false}
              name="year"
            />
          </FilterModalSection>
          <FilterModalSection title="Exterior Colour">
            {mapAttributesToCheckboxes("exteriorColour")}
          </FilterModalSection>
          <FilterModalSection title="Lifestyle">
            {mapAttributesToCheckboxes("lifestyle")}
          </FilterModalSection>
          <FilterModalSection title="Fuel Type">
            {mapAttributesToCheckboxes("fuelType")}
          </FilterModalSection>
          <FilterModalSection title="Drive Type">
            {mapAttributesToCheckboxes("driveType")}
          </FilterModalSection>
          <FilterModalSection title="Transmission" noDivider>
            {mapAttributesToCheckboxes("transmission")}
          </FilterModalSection>
        </DialogContent>
        <DialogActions className={classes.modalActions}>
          <ClearAllFiltersButton />
          <div className="flexGrow" />
          <SearchButton
            listingsCount={listingsCount}
            onClick={handleClose}
            data-cy="save-filter-modal"
          />
        </DialogActions>
      </Dialog>
    </span>
  );
};

FilterModal.propTypes = {
  attributes: PropTypes.shape({}).isRequired,
  clearInventoryCards: PropTypes.func,
  listingsCount: PropTypes.number,
  locationFormProps: PropTypes.shape({}).isRequired,
};

FilterModal.defaultProps = {
  clearInventoryCards: () => {},
  listingsCount: 0,
};

export default FilterModal;
